import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import { useAuth } from "../auth/AuthProvider"
import { cx } from "../lib/cx"
import type { TimeEntry, Client, Project } from "../types"

const INTERNAL_CATEGORIES = ["Sales", "Admin", "Meeting", "Training", "Other"]

const formatElapsed = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`
}

const formatDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })

const TimeTrackingPage = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const contractorParam = searchParams.get("contractor")
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, projectsRes, entriesRes] = await Promise.all([
          fetch("/api/clients"),
          fetch("/api/projects"),
          fetch("/api/time-entries")
        ])

        if (!clientsRes.ok || !projectsRes.ok || !entriesRes.ok) {
           console.error("Failed to fetch data")
           setIsLoading(false)
           return
        }

        const clientsData = (await clientsRes.json()) as { clients?: Client[] }
        const projectsData = (await projectsRes.json()) as { projects?: Project[] }
        const entriesData = (await entriesRes.json()) as { timeEntries?: TimeEntry[] }
        const fetchedClients = clientsData.clients || []
        const fetchedProjects = projectsData.projects || []
        const fetchedEntries = entriesData.timeEntries || []

        setClients(fetchedClients)
        setProjects(fetchedProjects)
        
        // Map IDs to names for display
        const mappedEntries = fetchedEntries.map((entry) => ({
             ...entry,
             client: fetchedClients.find((c: Client) => c.id === entry.client)?.name || entry.client,
             project: fetchedProjects.find((p: Project) => p.id === entry.project)?.name || entry.project
        }))
        setEntries(mappedEntries)
      } catch (e) {
        console.error("Failed to load data", e)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])
  
  // Live Timer State
  const [isRunning, setIsRunning] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [timerNote, setTimerNote] = useState("")
  const [timerType, setTimerType] = useState<"Internal" | "External">("External")
  const [timerClient, setTimerClient] = useState("")
  const [timerProject, setTimerProject] = useState("")
  const [timerCategory, setTimerCategory] = useState(INTERNAL_CATEGORIES[0])

  // Manual Entry State
  const [manualType, setManualType] = useState<"Internal" | "External">("External")
  const [manualClient, setManualClient] = useState("")
  const [manualProject, setManualProject] = useState("")
  const [manualCategory, setManualCategory] = useState(INTERNAL_CATEGORIES[0])
  const [manualDuration, setManualDuration] = useState("")
  const [manualNote, setManualNote] = useState("")

  const [filters, setFilters] = useState({
    contractor: contractorParam || "All",
    client: "All",
    project: "All",
    paid: "All",
    date: "",
  })

  useEffect(() => {
    if (contractorParam) {
      setFilters((prev) => ({ ...prev, contractor: contractorParam }))
    }
  }, [contractorParam])

  useEffect(() => {
    if (!isRunning) return
    const interval = window.setInterval(() => {
      setElapsedSeconds((current) => current + 1)
    }, 1000)
    return () => window.clearInterval(interval)
  }, [isRunning])

  const uniqueContractors = useMemo(
    () => ["All", ...new Set(entries.map((entry) => entry.contractor))],
    [entries],
  )
  const uniqueClients = useMemo(
    () => ["All", ...new Set(entries.filter(e => e.client).map((entry) => entry.client!))],
    [entries],
  )
  const uniqueProjects = useMemo(
    () => ["All", ...new Set(entries.filter(e => e.project).map((entry) => entry.project!))],
    [entries],
  )

  const filteredEntries = useMemo(
    () =>
      entries.filter((entry) => {
        if (filters.contractor !== "All" && entry.contractor !== filters.contractor)
          return false
        if (filters.client !== "All" && entry.client !== filters.client) return false
        if (filters.project !== "All" && entry.project !== filters.project) return false
        if (filters.paid === "Paid" && !entry.paid) return false
        if (filters.paid === "Unpaid" && entry.paid) return false
        if (filters.date && entry.date !== filters.date) return false
        return true
      }),
    [entries, filters],
  )

  const togglePaid = async (id: string) => {
    const entry = entries.find((e) => e.id === id)
    if (!entry) return
    const newPaid = !entry.paid

    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, paid: newPaid } : e)),
    )

    try {
      const res = await fetch(`/api/time-entries/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paid: newPaid }),
      })
      if (!res.ok) throw new Error("Failed")
    } catch {
      // Rollback
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, paid: !newPaid } : e)),
      )
    }
  }

  const exportCsv = () => {
    const header = ["Type", "Contractor", "Client", "Project", "Category", "Date", "Hours", "Paid", "Note"]
    const rows = filteredEntries.map((entry) => [
      entry.type,
      entry.contractor,
      entry.client || "",
      entry.project || "",
      entry.category || "",
      entry.date,
      entry.durationHours.toString(),
      entry.paid ? "Paid" : "Unpaid",
      `"${entry.note.replace(/"/g, '""')}"`,
    ])
    const csv = [header, ...rows].map((row) => row.join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "time-entries.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleManualSubmit = async () => {
    const duration = parseFloat(manualDuration) || 0
    if (duration === 0) return

    const newEntryPayload = {
      type: manualType,
      date: new Date().toISOString().split('T')[0],
      duration: duration,
      note: manualNote,
      ...(manualType === "External" 
        ? { client_id: manualClient, project_id: manualProject }
        : { category: manualCategory }
      )
    }

    try {
      const res = await fetch("/api/time-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newEntryPayload)
      })

      if (res.ok) {
        const data = await res.json()
        const newEntry: TimeEntry = {
          id: data.id,
          contractor: user?.name || "You",
          type: manualType,
          date: newEntryPayload.date,
          durationHours: duration,
          note: manualNote,
          paid: false,
          ...(manualType === "External" 
            ? { 
                client: clients.find(c => c.id === manualClient)?.name || manualClient, 
                project: projects.find(p => p.id === manualProject)?.name || manualProject 
              }
            : { category: manualCategory }
          )
        }
        setEntries(prev => [newEntry, ...prev])
        setManualDuration("")
        setManualNote("")
      }
    } catch (e) {
      console.error("Failed to save entry", e)
    }
  }

  const handleStop = async () => {
    const durationHours = elapsedSeconds / 3600
    // Prevent stopping if 0, but if slightly more than 0 allow it (and round up in backend/here)
    if (durationHours === 0) return

    // Pause UI first? Or wait for success? 
    // User complaint: "just pauses". 
    // We will optimistically pause, but if it fails we might want to alert.
    // However, keeping it paused allows them to resume if needed.
    setIsRunning(false)
    
    // Ensure at least 1 minute is logged if it's very short but > 0
    // Backend takes minutes. 
    // If we send raw hours, backend: Math.round(duration * 60)
    // 5 sec = 0.00138 hrs -> * 60 = 0.083 min -> round = 0.
    // We should enforce min duration or let backend handle it.
    // Let's send a minimum of 1 minute if it's non-zero.
    
    const durationToSend = durationHours < (1/60) ? (1/60) : durationHours;

    const newEntryPayload = {
      type: timerType,
      date: new Date().toISOString().split('T')[0],
      duration: durationToSend,
      note: timerNote,
      ...(timerType === "External" 
        ? { client_id: timerClient, project_id: timerProject }
        : { category: timerCategory }
      )
    }

    try {
      const res = await fetch("/api/time-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newEntryPayload)
      })
      
      if (res.ok) {
        const data = await res.json()
        const newEntry: TimeEntry = {
            id: data.id,
            contractor: user?.name || "You",
            type: timerType,
            date: newEntryPayload.date,
            durationHours: durationToSend,
            note: timerNote,
            paid: false,
            ...(timerType === "External" 
                ? { 
                    client: clients.find(c => c.id === timerClient)?.name || timerClient,
                    project: projects.find(p => p.id === timerProject)?.name || timerProject
                  }
                : { category: timerCategory }
              )
        }
        setEntries(prev => [newEntry, ...prev])
        setElapsedSeconds(0)
        setTimerNote("")
      } else {
        console.error("Failed to save entry, status:", res.status)
        alert("Failed to save time entry. Please try again.")
      }
    } catch (e) {
      console.error("Failed to save entry", e)
      alert("Failed to save time entry. Please check your connection.")
    }
  }

  const getProjectsForClient = (clientId: string) => {
    return projects.filter(p => p.clientId === clientId)
  }

  if (isLoading) {
      return <div className="flex h-64 items-center justify-center text-stone">Loading time entries...</div>
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-12 gap-6">
        {/* Live Timer Card */}
        <Card className="col-span-12 lg:col-span-7">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">Live Timer</p>
              <p className="text-sm text-stone">Track billable hours in real time.</p>
            </div>
            <StatusPill tone={isRunning ? "success" : "neutral"} label={isRunning ? "Running" : "Paused"} />
          </div>

          <div className="mt-4 flex gap-4 text-sm">
             <label className="flex items-center gap-2">
               <input 
                 type="radio" 
                 name="timerType" 
                 checked={timerType === "Internal"} 
                 onChange={() => setTimerType("Internal")}
                 disabled={isRunning}
               />
               Internal
             </label>
             <label className="flex items-center gap-2">
               <input 
                 type="radio" 
                 name="timerType" 
                 checked={timerType === "External"} 
                 onChange={() => setTimerType("External")}
                 disabled={isRunning}
               />
               External
             </label>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            <div className="text-3xl font-semibold text-slate-900">
              {formatElapsed(elapsedSeconds)}
            </div>
            
            {timerType === "Internal" ? (
              <select
                value={timerCategory}
                onChange={(e) => setTimerCategory(e.target.value)}
                className="rounded-full border border-line bg-white px-4 py-2 text-sm text-slate-900"
                disabled={isRunning}
              >
                {INTERNAL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            ) : (
              <>
                <select
                  value={timerClient}
                  onChange={(e) => {
                    setTimerClient(e.target.value)
                    setTimerProject("")
                  }}
                  className="rounded-full border border-line bg-white px-4 py-2 text-sm text-slate-900"
                  disabled={isRunning}
                >
                  <option value="" disabled>Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <select
                  value={timerProject}
                  onChange={(e) => setTimerProject(e.target.value)}
                  className="rounded-full border border-line bg-white px-4 py-2 text-sm text-slate-900"
                  disabled={isRunning || !timerClient}
                >
                   <option value="" disabled>Select Project</option>
                  {getProjectsForClient(timerClient).map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <input
              value={timerNote}
              onChange={(event) => setTimerNote(event.target.value)}
              placeholder="Add a note..."
              className="flex-1 rounded-full border border-line bg-white px-4 py-2 text-sm outline-none"
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsRunning(true)}
              className={cx(
                "rounded-full px-5 py-2 text-sm font-semibold",
                isRunning ? "bg-slate-100 text-slate-400" : "bg-moss text-white",
              )}
              disabled={isRunning || (timerType === "External" && (!timerClient || !timerProject))}
              title={timerType === "External" && (!timerClient || !timerProject) ? "Select a client and project first" : undefined}
            >
              Start timer
            </button>
            <button
              type="button"
              onClick={() => setIsRunning(false)}
              className={cx(
                "rounded-full px-5 py-2 text-sm font-semibold",
                !isRunning ? "bg-slate-100 text-slate-400" : "bg-sage text-white",
              )}
              disabled={!isRunning}
            >
              Pause
            </button>
            <button
              type="button"
              onClick={handleStop}
              className="rounded-full bg-red-500 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              disabled={elapsedSeconds === 0}
            >
              Stop
            </button>
            <button
              type="button"
              onClick={() => {
                setIsRunning(false)
                setElapsedSeconds(0)
                setTimerNote("")
              }}
              className="rounded-full bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-600"
            >
              Reset
            </button>
          </div>
        </Card>

        {/* Manual Entry Card */}
        <Card className="col-span-12 lg:col-span-5">
          <p className="text-lg font-semibold text-slate-900">Manual Entry</p>
          <p className="text-sm text-stone">Log hours without running the timer.</p>
          
          <div className="mt-4 flex gap-4 text-sm">
             <label className="flex items-center gap-2">
               <input 
                 type="radio" 
                 name="manualType" 
                 checked={manualType === "Internal"} 
                 onChange={() => setManualType("Internal")}
               />
               Internal
             </label>
             <label className="flex items-center gap-2">
               <input 
                 type="radio" 
                 name="manualType" 
                 checked={manualType === "External"} 
                 onChange={() => setManualType("External")}
               />
               External
             </label>
          </div>

          <div className="mt-4 space-y-3 text-sm">
            {manualType === "Internal" ? (
              <select
                value={manualCategory}
                onChange={(e) => setManualCategory(e.target.value)}
                className="w-full rounded-full border border-line bg-white px-4 py-2"
              >
                {INTERNAL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            ) : (
              <>
                <select
                  value={manualClient}
                  onChange={(e) => {
                    setManualClient(e.target.value)
                    setManualProject("")
                  }}
                  className="w-full rounded-full border border-line bg-white px-4 py-2"
                >
                  <option value="" disabled>Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <select
                  value={manualProject}
                  onChange={(e) => setManualProject(e.target.value)}
                  className="w-full rounded-full border border-line bg-white px-4 py-2"
                  disabled={!manualClient}
                >
                   <option value="" disabled>Select Project</option>
                  {getProjectsForClient(manualClient).map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </>
            )}

            <input
              value={manualDuration}
              onChange={(e) => setManualDuration(e.target.value)}
              placeholder="Duration (hours)"
              className="w-full rounded-full border border-line bg-white px-4 py-2"
              type="number"
              step="0.25"
            />
            <input
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
              placeholder="Notes"
              className="w-full rounded-full border border-line bg-white px-4 py-2"
            />
            <button
              type="button"
              onClick={handleManualSubmit}
              disabled={manualType === "External" && (!manualClient || !manualProject)}
              className="w-full rounded-full bg-moss px-4 py-2 font-semibold text-white disabled:opacity-50"
            >
              Add time entry
            </button>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold text-slate-900">Time Entries</p>
            <p className="text-sm text-stone">
              Filter by contractor, client, project, or paid status.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            Export CSV
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <select
            value={filters.contractor}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, contractor: event.target.value }))
            }
            className="rounded-full border border-line bg-white px-4 py-2"
          >
            {uniqueContractors.map((contractor) => (
              <option key={contractor} value={contractor}>
                {contractor}
              </option>
            ))}
          </select>
          <select
            value={filters.client}
            onChange={(event) => setFilters((prev) => ({ ...prev, client: event.target.value }))}
            className="rounded-full border border-line bg-white px-4 py-2"
          >
            {uniqueClients.map((client) => (
              <option key={client} value={client}>
                {client}
              </option>
            ))}
          </select>
          <select
            value={filters.project}
            onChange={(event) => setFilters((prev) => ({ ...prev, project: event.target.value }))}
            className="rounded-full border border-line bg-white px-4 py-2"
          >
            {uniqueProjects.map((project) => (
              <option key={project} value={project}>
                {project}
              </option>
            ))}
          </select>
          <select
            value={filters.paid}
            onChange={(event) => setFilters((prev) => ({ ...prev, paid: event.target.value }))}
            className="rounded-full border border-line bg-white px-4 py-2"
          >
            {["All", "Paid", "Unpaid"].map((paid) => (
              <option key={paid} value={paid}>
                {paid}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filters.date}
            onChange={(event) => setFilters((prev) => ({ ...prev, date: event.target.value }))}
            className="rounded-full border border-line bg-white px-4 py-2"
          />
        </div>
        <div className="mt-5 space-y-3">
          {filteredEntries.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white px-4 py-3 text-sm"
            >
              <div>
                {entry.type === "Internal" ? (
                   <>
                     <p className="font-semibold text-slate-900">{entry.category}</p>
                     <p className="text-xs text-stone">
                       {entry.contractor} · Internal · {formatDate(entry.date)}
                     </p>
                   </>
                ) : (
                  <>
                    <p className="font-semibold text-slate-900">{entry.project}</p>
                    <p className="text-xs text-stone">
                      {entry.contractor} · {entry.client} · {formatDate(entry.date)}
                    </p>
                  </>
                )}
                <p className="text-xs text-stone">{entry.note}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-slate-900">{entry.durationHours.toFixed(2)} hrs</p>
                <button
                  type="button"
                  onClick={() => togglePaid(entry.id)}
                  className="mt-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
                >
                  Mark {entry.paid ? "unpaid" : "paid"}
                </button>
              </div>
              <StatusPill tone={entry.paid ? "success" : "warning"} label={entry.paid ? "Paid" : "Unpaid"} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default TimeTrackingPage
